import { isQualifiedEvidence, type ExerciseAttempt, type ExerciseSource } from '@/entities/attempt'
import type { AbilityDimension } from '@/entities/question/types'
import type { LearningLayer, SkillDimension } from '@/types'
import type { StudentLearningProfile } from './types'

const abilityDimensions: AbilityDimension[] = [
  'listening', 'speaking', 'reading', 'writing', 'vocabulary', 'grammar', 'phonics',
]
const measuredDimensions: SkillDimension[] = ['listening', 'speaking', 'reading', 'writing']
const minimumLevelEvidence = 8
const minimumAbilityEvidence = 3
const recentWindowSize = 12
const minimumLevelEvidenceWeight = 8
const minimumAbilityEvidenceWeight = 3
const vocabularyTerms = ['vocabulary', 'vocab', 'word', '单词', '词汇']
const grammarTerms = ['grammar', 'sentence', '语法', '句型']
const phonicsTerms = ['phonics', 'spelling', '拼读', '自拼', '语音']
const learningDimensionByAbility: Record<AbilityDimension, SkillDimension> = {
  listening: 'listening',
  speaking: 'speaking',
  reading: 'reading',
  writing: 'writing',
  vocabulary: 'writing',
  grammar: 'writing',
  phonics: 'listening',
}

export type DynamicAssessmentStatus = 'baseline' | 'collecting' | 'stable' | 'adjusted'

export interface DynamicAbilitySnapshot {
  baselineScore: number
  currentScore: number
  ongoingScore: number | null
  baselineSampleCount: number
  ongoingSampleCount: number
  confidence: number
}

export interface DynamicAssessmentSnapshot {
  status: DynamicAssessmentStatus
  baselineLevel: LearningLayer
  recommendedLevel: LearningLayer
  /** C 层正确率低于 60% 时滞留本单元并进入强化阶段（材料《学习规划板块功能需求文档》4.3）。 */
  reinforceUnit: boolean
  evidenceCount: number
  recentAccuracy: number | null
  latestEvidenceAt: string | null
  sources: ExerciseSource[]
  abilities: Record<AbilityDimension, DynamicAbilitySnapshot>
  strongAbilities: AbilityDimension[]
  supportAbilities: AbilityDimension[]
  reason: string
}

export function getLearningSupportDimensions(
  abilities: readonly AbilityDimension[],
): SkillDimension[] {
  return [...new Set(abilities.map((ability) => learningDimensionByAbility[ability]))]
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

export function getAttemptProfileDimensions(attempt: ExerciseAttempt): AbilityDimension[] {
  const dimensions = new Set<AbilityDimension>()
  if (attempt.ability) dimensions.add(attempt.ability)
  const text = [attempt.category, attempt.subCategory, attempt.sceneType]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (vocabularyTerms.some((term) => text.includes(term))) dimensions.add('vocabulary')
  if (grammarTerms.some((term) => text.includes(term))) dimensions.add('grammar')
  if (phonicsTerms.some((term) => text.includes(term))) dimensions.add('phonics')
  return [...dimensions]
}

function getAttemptScore(attempt: ExerciseAttempt): number {
  if (typeof attempt.score === 'number' && Number.isFinite(attempt.score)) {
    return clamp(attempt.score, 0, 100)
  }
  return attempt.correct ? 100 : 0
}

/**
 * Measures how independently an answer was produced without treating support as failure.
 * Low-quality evidence remains visible in reports, but needs more repetitions before it can
 * move a level or dominate the rolling ability estimate.
 */
export function getDynamicEvidenceWeight(attempt: ExerciseAttempt): number {
  const durationWeight = attempt.durationMs < 500 ? 0.35 : attempt.durationMs < 900 ? 0.7 : 1
  if (!attempt.correct) return durationWeight

  const hintWeight = Math.pow(0.68, attempt.hintCount)
  const retryWeight = Math.pow(0.68, attempt.retryCount)
  const methodWeight = attempt.scoringMethod === 'self-check' ? 0.5 : 1
  return Number(Math.max(0.15, durationWeight * hintWeight * retryWeight * methodWeight).toFixed(3))
}

function sumEvidenceWeight(attempts: readonly ExerciseAttempt[]): number {
  return attempts.reduce((sum, attempt) => sum + getDynamicEvidenceWeight(attempt), 0)
}

function getWeightedAccuracy(attempts: readonly ExerciseAttempt[]): number | null {
  const totalWeight = sumEvidenceWeight(attempts)
  if (totalWeight === 0) return null
  return Math.round(attempts.reduce((sum, attempt) => (
    sum + (getAttemptScore(attempt) * getDynamicEvidenceWeight(attempt))
  ), 0) / totalWeight)
}

function isTrustedEvidence(attempt: ExerciseAttempt): boolean {
  return isQualifiedEvidence(attempt)
}

function isLevelEvidence(attempt: ExerciseAttempt): boolean {
  return isQualifiedEvidence(attempt)
    && attempt.evidenceKind !== 'remediation-training'
}

function isAbilityEvidence(attempt: ExerciseAttempt): boolean {
  return isQualifiedEvidence(attempt)
}

function combineAbility(
  profile: StudentLearningProfile,
  dimension: AbilityDimension,
  attempts: ExerciseAttempt[],
): DynamicAbilitySnapshot {
  const baseline = profile.ability[dimension]
  const matching = attempts.filter((attempt) => (
    isAbilityEvidence(attempt) && getAttemptProfileDimensions(attempt).includes(dimension)
  ))
  const evidenceWeight = sumEvidenceWeight(matching)
  const ongoingScore = matching.length === 0
    ? null
    : getWeightedAccuracy(matching)
  const baselineWeight = clamp(baseline.sampleCount, 0, 4)
  const ongoingWeight = Math.min(evidenceWeight, recentWindowSize)
  const currentScore = ongoingScore === null
    ? baseline.score
    : Math.round(
      ((baseline.score * baselineWeight) + (ongoingScore * ongoingWeight))
      / (baselineWeight + ongoingWeight),
    )
  const confidenceGain = Math.min(0.4, evidenceWeight * 0.05)

  return {
    baselineScore: baseline.score,
    currentScore,
    ongoingScore,
    baselineSampleCount: baseline.sampleCount,
    ongoingSampleCount: matching.length,
    confidence: Number(Math.min(0.95, baseline.confidence + confidenceGain).toFixed(2)),
  }
}

function moveOneLevel(
  baseline: LearningLayer,
  direction: 'up' | 'down',
): LearningLayer {
  if (direction === 'up') return baseline === 'C' ? 'B' : 'A'
  return baseline === 'A' ? 'B' : 'C'
}

function recommendLevel(
  baseline: LearningLayer,
  attempts: ExerciseAttempt[],
  recentAccuracy: number | null,
): { level: LearningLayer; reinforceUnit: boolean } {
  const measuredAbilityCount = measuredDimensions.filter((dimension) => (
    sumEvidenceWeight(
      attempts.filter((attempt) => getAttemptProfileDimensions(attempt).includes(dimension)),
    ) >= minimumAbilityEvidenceWeight
  )).length

  if (attempts.length < minimumLevelEvidence
    || sumEvidenceWeight(attempts) < minimumLevelEvidenceWeight
    || measuredAbilityCount < 2
    || recentAccuracy === null) {
    return { level: baseline, reinforceUnit: false }
  }

  // 材料《学习规划板块功能需求文档》4.3 分层判定规则（按当前层级逐档判定）
  switch (baseline) {
    case 'A':
      if (recentAccuracy >= 90) return { level: 'A', reinforceUnit: false }  // 保留 A，解锁拓展进阶
      if (recentAccuracy < 75) return { level: moveOneLevel(baseline, 'down'), reinforceUnit: false }
      return { level: 'A', reinforceUnit: false }
    case 'B':
      if (recentAccuracy >= 85) return { level: moveOneLevel(baseline, 'up'), reinforceUnit: false }
      if (recentAccuracy < 70) return { level: moveOneLevel(baseline, 'down'), reinforceUnit: false }
      return { level: 'B', reinforceUnit: false }
    case 'C':
      if (recentAccuracy >= 75) return { level: moveOneLevel(baseline, 'up'), reinforceUnit: false }
      if (recentAccuracy < 60) return { level: 'C', reinforceUnit: true }  // 滞留 C，进入本单元强化阶段
      return { level: 'C', reinforceUnit: false }
  }
}

function hasSufficientLevelEvidence(attempts: ExerciseAttempt[]): boolean {
  const measuredAbilityCount = measuredDimensions.filter((dimension) => (
    sumEvidenceWeight(
      attempts.filter((attempt) => getAttemptProfileDimensions(attempt).includes(dimension)),
    ) >= minimumAbilityEvidenceWeight
  )).length
  return attempts.length >= minimumLevelEvidence
    && sumEvidenceWeight(attempts) >= minimumLevelEvidenceWeight
    && measuredAbilityCount >= 2
}

function createReason(
  status: DynamicAssessmentStatus,
  evidenceCount: number,
  recentAccuracy: number | null,
  level: LearningLayer,
  baseline: LearningLayer,
  reinforceUnit: boolean,
): string {
  if (status === 'baseline') return '当前仅使用前测基线；完成后续评测和学习任务后会持续校准。'
  if (status === 'collecting') {
    return `已接入 ${evidenceCount} 次后续作答，证据仍在积累，暂时保持前测层级。`
  }
  if (reinforceUnit) {
    return `已接入 ${evidenceCount} 次后续作答（近期正确率 ${recentAccuracy}%），正确率低于 60%，请先完成本单元强化阶段，再进入下一单元。`
  }
  if (status === 'adjusted') {
    return `根据 ${evidenceCount} 次后续作答（近期正确率 ${recentAccuracy}%），当前学习安排调整为 ${level} 层。`
  }
  if (baseline === 'A' && level === 'A' && (recentAccuracy ?? 0) >= 90) {
    return `已接入 ${evidenceCount} 次后续作答（近期正确率 ${recentAccuracy}%），保持 A 层，已解锁拓展进阶任务。`
  }
  return `已接入 ${evidenceCount} 次后续作答（近期正确率 ${recentAccuracy}%），当前层级保持稳定。`
}

export function createDynamicAssessmentSnapshot(
  profile: StudentLearningProfile,
  attempts: readonly ExerciseAttempt[],
): DynamicAssessmentSnapshot {
  const baselineTime = new Date(profile.updatedAt).getTime()
  const learnerAttempts = attempts
    .filter((attempt) => (
      attempt.studentId === profile.identity.id
      && isTrustedEvidence(attempt)
      && Number.isFinite(new Date(attempt.completedAt).getTime())
      && new Date(attempt.completedAt).getTime() > baselineTime
    ))
    .sort((left, right) => (
      new Date(left.completedAt).getTime() - new Date(right.completedAt).getTime()
    ))
  const recentAttempts = learnerAttempts.slice(-recentWindowSize)
  const levelAttempts = recentAttempts.filter(isLevelEvidence)
  const recentAccuracy = getWeightedAccuracy(levelAttempts)
  const abilities = Object.fromEntries(abilityDimensions.map((dimension) => [
    dimension,
    combineAbility(profile, dimension, learnerAttempts.slice(-recentWindowSize)),
  ])) as Record<AbilityDimension, DynamicAbilitySnapshot>
  const levelDecision = recommendLevel(profile.recommendedLevel, levelAttempts, recentAccuracy)
  const recommendedLevel = levelDecision.level
  const qualifiedAbilities = abilityDimensions.filter((dimension) => (
    abilities[dimension].ongoingSampleCount >= minimumAbilityEvidence
  ))
  const strongAbilities = [...qualifiedAbilities]
    .filter((dimension) => abilities[dimension].currentScore >= 80)
    .sort((left, right) => abilities[right].currentScore - abilities[left].currentScore)
    .slice(0, 2)
  const supportAbilities = abilityDimensions
    .filter((dimension) => (
      abilities[dimension].currentScore < 70
      && (
        abilities[dimension].baselineScore < 70
        || abilities[dimension].ongoingSampleCount >= minimumAbilityEvidence
      )
    ))
    .sort((left, right) => abilities[left].currentScore - abilities[right].currentScore)
    .slice(0, 2)
  const status: DynamicAssessmentStatus = learnerAttempts.length === 0
    ? 'baseline'
    : !hasSufficientLevelEvidence(levelAttempts)
      ? 'collecting'
      : recommendedLevel === profile.recommendedLevel ? 'stable' : 'adjusted'

  return {
    status,
    baselineLevel: profile.recommendedLevel,
    recommendedLevel,
    reinforceUnit: levelDecision.reinforceUnit,
    evidenceCount: learnerAttempts.length,
    recentAccuracy,
    latestEvidenceAt: learnerAttempts[learnerAttempts.length - 1]?.completedAt ?? null,
    sources: [...new Set(learnerAttempts.map((attempt) => attempt.source))],
    abilities,
    strongAbilities,
    supportAbilities,
    reason: createReason(status, learnerAttempts.length, recentAccuracy, recommendedLevel, profile.recommendedLevel, levelDecision.reinforceUnit),
  }
}
